/**
 * Where the player was, per browser tab (sessionStorage), so a reload — the
 * automatic update included — opens the same page again instead of the plain
 * route in the address bar (which lost e.g. hero mode in the creator).
 */

const KEY = 'paper-legends/resume';
/** Scenes that can be reopened from their params (a battle cannot). */
const RESUMABLE = new Set(['menu', 'notebook', 'creator', 'help', 'multiplayer']);

export interface ResumePoint {
  name: string;
  params: unknown;
}

export function rememberScene(name: string, params: unknown): void {
  if (!RESUMABLE.has(name)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ name, params: params ?? null }));
  } catch {
    /* storage blocked: reloads open the address-bar route */
  }
}

export function lastScene(): ResumePoint | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as ResumePoint;
    return typeof p?.name === 'string' && RESUMABLE.has(p.name) ? p : null;
  } catch {
    return null;
  }
}

/* ---------- the creator's unsaved page ---------- */

const DRAFT_KEY = 'paper-legends/creator-draft';

export interface CreatorDraft {
  heroMode: boolean;
  /** The monster or hero being edited (none = a new one). */
  id: string | null;
  drawing: unknown;
}

export function saveDraft(d: CreatorDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* too big or blocked: no draft */
  }
}

/** The draft for this creator page (same mode and same monster/hero), if any. */
export function takeDraft(heroMode: boolean, id: string | null): CreatorDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as CreatorDraft;
    return d.heroMode === heroMode && d.id === id ? d : null;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}
